<template>
  <section class="cart-section mt-5">
    <div class="container mt-5 cart-container-wrapper">

      <!-- Page Header -->
      <div class="cart-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="/productos" class="btn-back">
            <i class="fas fa-arrow-left"></i>
          </a>
          <div>
            <h1 class="cart-page-title font-good-brush">Mi Carrito</h1>
            <p class="cart-page-subtitle" v-if="cartItems.length > 0">
              {{ cartQuantity }} {{ cartQuantity === 1 ? 'producto' : 'productos' }}
            </p>
          </div>
        </div>
      </div>

      <div v-if="globalLoading" class="cart-loading-banner" role="status" aria-live="polite">
        <i class="fas fa-spinner fa-spin me-2"></i>
        <span>{{ globalLoadingText }}</span>
      </div>

      <!-- Empty Cart State -->
      <div v-if="cartItems.length === 0" class="empty-cart-state">
        <div class="empty-cart-icon">
          <i class="fas fa-shopping-cart"></i>
        </div>
        <h2 class="empty-cart-title">Tu carrito está vacío</h2>
        <p class="empty-cart-text">¡Descubrí nuestros productos y comenzá tu compra!</p>
        <a class="btn btn-primary btn-lg px-5" href="/productos">
          <i class="fas fa-store me-2"></i>
          Explorar Productos
        </a>
      </div>

      <!-- Cart Content -->
      <div v-else class="row g-4 cart-content">

        <!-- Left: Cart Items -->
        <div class="col-lg-7">
          <div class="cart-items-panel">
            <TransitionGroup name="list" tag="div" class="d-flex flex-column gap-3">
              <div
                v-for="item in cartItems"
                :key="item.lineId || item.id"
                class="cart-item-card"
                :class="{ 'coupon-eligible': isItemEligibleForCoupon(item) }"
              >
                <!-- Coupon eligible ribbon -->
                <div v-if="appliedCoupon && isItemEligibleForCoupon(item)" class="coupon-ribbon">
                  <i class="fas fa-tag me-1"></i>Descuento aplicado
                </div>

                <div class="cart-item-inner">
                  <!-- Product Image -->
                  <div class="cart-item-image-wrap">
                    <img
                      :src="item.image || require('@/assets/images/logo.png')"
                      :alt="item.name"
                      class="cart-item-image"
                    />
                    <span v-if="item.total_discount > 0" class="item-discount-pill">
                      -{{ Math.round(item.total_discount) }}%
                    </span>
                  </div>

                  <!-- Product Info -->
                  <div class="cart-item-info">
                    <div class="cart-item-header">
                      <div class="cart-item-name-wrap">
                        <h4 class="cart-item-name">{{ item.name }}</h4>
                        <span v-if="item.brand" class="cart-item-brand">{{ item.brand }}</span>
                        <!-- Variants -->
                        <div v-if="item.variants && Object.keys(item.variants).length" class="cart-item-variants">
                          <span
                            v-for="(opt, vname) in item.variants"
                            :key="`${item.id}-${vname}-${opt}`"
                            class="variant-chip"
                          >{{ vname }}: {{ opt }}</span>
                        </div>
                        <!-- Special promotion badge -->
                        <span
                          v-if="item.has_special_promotion && item.special_promotion_name"
                          class="promo-badge"
                        >
                          <i class="fas fa-star me-1"></i>{{ item.special_promotion_name }}
                        </span>
                      </div>
                      <button
                        class="btn-remove-item"
                        @click="removeItem(item.lineId || item.id)"
                        title="Eliminar producto"
                      >
                        <i class="fas fa-times"></i>
                      </button>
                    </div>

                    <!-- Price + Quantity Row -->
                    <div class="cart-item-footer">
                      <!-- Quantity stepper -->
                      <div class="qty-stepper">
                        <button
                          class="qty-btn"
                          type="button"
                          @click="updateQuantity(item.lineId || item.id, item.quantity - 1)"
                          :disabled="item.quantity <= 1"
                        >
                          <i class="fas fa-minus"></i>
                        </button>
                        <input
                          type="number"
                          class="qty-input"
                          :value="item.quantity"
                          min="1"
                          max="99"
                          @input="(e) => {
                            const value = e.target.value;
                            if (value && !isNaN(value) && value.trim() !== '') {
                              updateQuantity(item.lineId || item.id, parseInt(value, 10));
                            } else {
                              e.target.value = item.quantity;
                            }
                          }"
                          @blur="(e) => {
                            if (!e.target.value || e.target.value.trim() === '' || isNaN(e.target.value)) {
                              e.target.value = item.quantity;
                            }
                          }"
                        />
                        <button
                          class="qty-btn"
                          type="button"
                          @click="updateQuantity(item.lineId || item.id, item.quantity + 1)"
                          :disabled="item.stock && item.quantity >= item.stock"
                        >
                          <i class="fas fa-plus"></i>
                        </button>
                      </div>

                      <!-- Price block -->
                      <div class="cart-item-price-block">
                        <div v-if="item.total_discount > 0 && item.original_price" class="item-original-price">
                          <template v-if="item.currency === 'USD'">USD {{ item.original_price * item.quantity }}</template>
                          <template v-else>{{ formatPrice(item.original_price * item.quantity) }}</template>
                        </div>
                        <div class="item-final-price">
                          <template v-if="item.currency === 'USD'">
                            <span
                              class="usd-price"
                              :title="dollarRate && dollarRate.value ? `Estimado: ${formatPrice(convertUsdToArs(item.price * item.quantity))}` : 'Se convertirá a pesos al finalizar'"
                            >
                              USD {{ item.price * item.quantity }}
                              <i class="fas fa-info-circle ms-1 text-muted" style="font-size:0.7em"></i>
                            </span>
                            <span v-if="dollarRate && dollarRate.value" class="ars-equiv">
                              ≈ {{ formatPrice(convertUsdToArs(item.price * item.quantity)) }}
                            </span>
                          </template>
                          <template v-else>
                            {{ formatPrice(item.price * item.quantity) }}
                          </template>
                        </div>
                        <div class="item-unit-price">
                          <template v-if="item.currency === 'USD'">USD {{ item.price }} c/u</template>
                          <template v-else>{{ formatPrice(item.price) }} c/u</template>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TransitionGroup>
          </div>
        </div>

        <!-- Right: Order Summary -->
        <div class="col-lg-5">
          <div class="summary-panel">

            <!-- Checkout Form -->
            <form v-if="showCheckoutForm" @submit.prevent="submitOrder" class="checkout-form">
              <!-- Step indicator -->
              <div class="checkout-steps mb-4">
                <div class="step active">
                  <span class="step-num">1</span>
                  <span class="step-label">Datos</span>
                </div>
                <div class="step-line"></div>
                <div class="step active">
                  <span class="step-num">2</span>
                  <span class="step-label">Entrega</span>
                </div>
                <div class="step-line"></div>
                <div class="step">
                  <span class="step-num">3</span>
                  <span class="step-label">Pago</span>
                </div>
              </div>

              <!-- Recipient Information -->
              <div class="form-section mb-4">
                <div class="form-section-header">
                  <div class="form-section-icon">
                    <i class="fas fa-user"></i>
                  </div>
                  <h6 class="form-section-title">Datos del destinatario</h6>
                </div>
                <div class="row g-3">
                  <div class="col-12">
                    <label class="form-label">Nombre completo *</label>
                    <div class="input-with-icon">
                      <i class="fas fa-user input-icon"></i>
                      <input
                        v-model="orderData.recipient_name"
                        type="text"
                        class="form-control form-control-icon"
                        placeholder="Nombre de quien recibe"
                        required
                      />
                    </div>
                    <div class="field-error" v-if="v$.recipient_name.$error">
                      {{ v$.recipient_name.$errors[0].$message }}
                    </div>
                  </div>
                  <div class="col-sm-6">
                    <label class="form-label">DNI / CUIT *</label>
                    <div class="input-with-icon">
                      <i class="fas fa-id-card input-icon"></i>
                      <input
                        v-model="orderData.customer_dni_cuit"
                        type="text"
                        class="form-control form-control-icon"
                        placeholder="Ej: 12345678"
                        required
                      />
                    </div>
                    <div class="field-error" v-if="v$.customer_dni_cuit.$error">
                      {{ v$.customer_dni_cuit.$errors[0].$message }}
                    </div>
                  </div>
                  <div class="col-sm-6">
                    <label class="form-label">Teléfono *</label>
                    <div class="input-with-icon">
                      <i class="fas fa-phone input-icon"></i>
                      <input
                        v-model="orderData.customer_phone"
                        type="tel"
                        class="form-control form-control-icon"
                        placeholder="Ej: 1123456789"
                        required
                      />
                    </div>
                    <div class="field-error" v-if="v$.customer_phone.$error">
                      {{ v$.customer_phone.$errors[0].$message }}
                    </div>
                  </div>
                  <div class="col-12">
                    <label class="form-label">Email *</label>
                    <div class="input-with-icon">
                      <i class="fas fa-envelope input-icon"></i>
                      <input
                        v-model="orderData.customer_email"
                        type="email"
                        class="form-control form-control-icon"
                        placeholder="ejemplo@gmail.com"
                        required
                      />
                    </div>
                    <div class="field-error" v-if="v$.customer_email.$error">
                      {{ v$.customer_email.$errors[0].$message }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Delivery Options -->
              <div class="form-section mb-4">
                <div class="form-section-header">
                  <div class="form-section-icon">
                    <i class="fas fa-truck"></i>
                  </div>
                  <h6 class="form-section-title">Entrega y pago</h6>
                </div>
                <div class="delivery-cards">
                  <label
                    class="delivery-card"
                    :class="{ selected: orderData.payment_delivery_option === 'pickup_cash' }"
                    for="option1"
                  >
                    <input type="radio" id="option1" v-model="orderData.payment_delivery_option" value="pickup_cash" class="delivery-card-radio" />
                    <div class="delivery-card-icons">
                      <i class="fas fa-store"></i>
                      <i class="fas fa-money-bill-wave"></i>
                    </div>
                    <div class="delivery-card-text">
                      <span class="delivery-card-title">Retiro en tienda</span>
                      <span class="delivery-card-desc">Pago en efectivo</span>
                    </div>
                    <div class="delivery-card-check"><i class="fas fa-check-circle"></i></div>
                  </label>

                  <label
                    class="delivery-card"
                    :class="{ selected: orderData.payment_delivery_option === 'pickup_online' }"
                    for="option2"
                  >
                    <input type="radio" id="option2" v-model="orderData.payment_delivery_option" value="pickup_online" class="delivery-card-radio" />
                    <div class="delivery-card-icons">
                      <i class="fas fa-store"></i>
                      <i class="fas fa-credit-card"></i>
                    </div>
                    <div class="delivery-card-text">
                      <span class="delivery-card-title">Retiro en tienda</span>
                      <span class="delivery-card-desc">Pago por MercadoPago</span>
                    </div>
                    <div class="delivery-card-check"><i class="fas fa-check-circle"></i></div>
                  </label>

                  <label
                    class="delivery-card"
                    :class="{ selected: orderData.payment_delivery_option === 'delivery_cash' }"
                    for="option3"
                  >
                    <input type="radio" id="option3" v-model="orderData.payment_delivery_option" value="delivery_cash" class="delivery-card-radio" />
                    <div class="delivery-card-icons">
                      <i class="fas fa-truck"></i>
                      <i class="fas fa-handshake"></i>
                    </div>
                    <div class="delivery-card-text">
                      <span class="delivery-card-title">Envío a domicilio</span>
                      <span class="delivery-card-desc">Acordar pago</span>
                    </div>
                    <div class="delivery-card-check"><i class="fas fa-check-circle"></i></div>
                  </label>

                  <label
                    class="delivery-card"
                    :class="{ selected: orderData.payment_delivery_option === 'delivery_online' }"
                    for="option4"
                  >
                    <input type="radio" id="option4" v-model="orderData.payment_delivery_option" value="delivery_online" class="delivery-card-radio" />
                    <div class="delivery-card-icons">
                      <i class="fas fa-truck"></i>
                      <i class="fas fa-credit-card"></i>
                    </div>
                    <div class="delivery-card-text">
                      <span class="delivery-card-title">Envío a domicilio</span>
                      <span class="delivery-card-desc">Pago por MercadoPago</span>
                    </div>
                    <div class="delivery-card-check"><i class="fas fa-check-circle"></i></div>
                  </label>
                </div>

                <!-- Address Fields -->
                <div v-if="showAddressFields" class="address-section mt-3">
                  <div class="address-fields-grid">
                    <div class="row g-2">
                      <div class="col-8">
                        <label class="form-label">Calle *</label>
                        <input v-model="addressFields.street" type="text" class="form-control" placeholder="Nombre de la calle" required />
                      </div>
                      <div class="col-4">
                        <label class="form-label">Número *</label>
                        <input v-model="addressFields.number" type="text" class="form-control" placeholder="123" required />
                      </div>
                      <div class="col-6">
                        <label class="form-label">Barrio</label>
                        <input v-model="addressFields.neighborhood" type="text" class="form-control" placeholder="Barrio" />
                      </div>
                      <div class="col-6">
                        <label class="form-label">Ciudad *</label>
                        <input v-model="addressFields.city" type="text" class="form-control" placeholder="Ciudad" required />
                      </div>
                      <div class="col-6">
                        <label class="form-label">Código Postal *</label>
                        <input v-model="addressFields.postalCode" type="text" class="form-control" placeholder="1234" required />
                      </div>
                      <div class="col-12">
                        <label class="form-label">Detalles adicionales</label>
                        <textarea v-model="addressFields.details" class="form-control" rows="2" placeholder="Piso, dpto, entre calles... (opcional)"></textarea>
                      </div>
                    </div>
                  </div>

                  <!-- Cotización de envío (Correo Argentino) -->
                  <ShippingQuote
                    v-if="shippingEnabled"
                    theme="light"
                    :external-postal-code="addressFields.postalCode"
                    :declared-value="cartSubtotal"
                    :auto-select="true"
                    start-expanded
                    @rate-selected="onShippingRateSelected"
                  />

                  <div v-if="requiresBranchSelection" class="delivery-info-msg warning mt-2">
                    <i class="fas fa-store-alt me-2"></i>
                    Para envíos a sucursal, seleccioná la sucursal de retiro.
                  </div>
                </div>

                <!-- Info messages -->
                <div v-if="showPickupMessage" class="delivery-info-msg info mt-3">
                  <i class="fas fa-info-circle me-2"></i>
                  Te contactaremos por email para coordinar la entrega en nuestra tienda.
                </div>
                <div v-if="showCashMessage" class="delivery-info-msg warning mt-3">
                  <i class="fas fa-money-bill me-2"></i>
                  Deberás pagar en efectivo al momento de la entrega.
                </div>
                <div v-if="showOnlinePaymentMessage" class="delivery-info-msg success mt-3">
                  <i class="fas fa-shield-alt me-2"></i>
                  Serás redirigido a MercadoPago para completar tu pago de forma segura.
                </div>
              </div>
            </form>

            <!-- Summary Details -->
            <div class="summary-details-panel">
              <h3 class="summary-title">Resumen</h3>

              <div class="summary-line">
                <span class="summary-line-label">Subtotal</span>
                <span class="summary-line-value">{{ formatPrice(cartSubtotal) }}</span>
              </div>

              <!-- Shipping cost (when Correo Argentino rate selected) -->
              <div v-if="selectedShippingRate && orderData.payment_delivery_option?.startsWith('delivery')" class="summary-line">
                <span class="summary-line-label">Envío</span>
                <span class="summary-line-value">
                  {{ selectedShippingCost > 0 ? formatPrice(selectedShippingCost) : 'GRATIS' }}
                </span>
              </div>

              <!-- Coupon section -->
              <div class="coupon-section">
                <div v-if="!appliedCoupon" class="coupon-input-wrap">
                  <div class="coupon-input-group">
                    <i class="fas fa-ticket-alt coupon-icon"></i>
                    <input
                      type="text"
                      class="coupon-input"
                      v-model="couponCode"
                      placeholder="Código de cupón"
                      :disabled="validatingCoupon"
                      @keyup.enter="validateCouponCode"
                    />
                    <button
                      class="coupon-apply-btn"
                      type="button"
                      @click="validateCouponCode"
                      :disabled="!couponCode.trim() || validatingCoupon"
                    >
                      <i v-if="validatingCoupon" class="fas fa-spinner fa-spin"></i>
                      <span v-else>Aplicar</span>
                    </button>
                  </div>
                </div>
                <div v-else class="coupon-applied">
                  <div class="coupon-applied-info">
                    <i class="fas fa-tag me-2 text-brand"></i>
                    <div>
                      <strong class="coupon-code-text">{{ appliedCoupon.code }}</strong>
                      <span class="coupon-desc ms-1">{{ getCouponDisplayInfo(appliedCoupon) }}</span>
                      <div v-if="discountInfo && discountInfo.hasNonEligibleItems" class="coupon-partial-note">
                        Aplicado a {{ discountInfo.eligibleCount }} de {{ discountInfo.totalItems }} productos
                      </div>
                    </div>
                  </div>
                  <button class="coupon-remove-btn" @click="removeCoupon" title="Quitar cupón">
                    <i class="fas fa-times"></i>
                  </button>
                </div>
              </div>

              <!-- Discount line -->
              <div v-if="appliedCoupon" class="summary-line discount-line">
                <span class="summary-line-label">
                  <i class="fas fa-tag me-1 text-brand"></i>Descuento
                </span>
                <span class="summary-line-value text-brand">-{{ formatPrice(discountAmount) }}</span>
              </div>

              <!-- Total -->
              <div class="summary-total-row">
                <span class="summary-total-label">Total</span>
                <div class="summary-total-value">
                  <template v-if="cartItems.some(item => item.currency === 'USD')">
                    <span v-if="dollarRate && dollarRate.value">
                      <span v-if="discountAmount > 0" class="summary-total-original">
                        {{ formatPrice(cartTotalArs + selectedShippingCost) }}
                      </span>
                      {{ formatPrice((cartTotalArs - discountAmount) + selectedShippingCost) }}
                    </span>
                    <span v-else class="text-warning small">
                      Falta cotización del dólar
                    </span>
                  </template>
                  <template v-else>
                    <span v-if="discountAmount > 0" class="summary-total-original">
                      {{ formatPrice(cartSubtotal + selectedShippingCost) }}
                    </span>
                    {{ formatPrice(finalTotal) }}
                  </template>
                </div>
              </div>

              <!-- Min purchase message -->
              <div v-if="minPurchaseAmount > 0" class="min-purchase-bar">
                <div class="min-purchase-info" :class="isMinPurchaseMet ? 'met' : 'unmet'">
                  <i :class="['fas', isMinPurchaseMet ? 'fa-check-circle' : 'fa-exclamation-circle']"></i>
                  <template v-if="cartItems.some(item => item.currency === 'USD')">
                    <span v-if="dollarRate && dollarRate.value">
                      Mínimo ARS ${{ minPurchaseAmount }} {{ isMinPurchaseMet ? '✓' : `— faltan ${ (minPurchaseAmount - cartTotalArs).toFixed(0) }` }}
                    </span>
                    <span v-else class="text-warning">Sin cotización del dólar</span>
                  </template>
                  <template v-else>
                    <span v-if="isMinPurchaseMet">Mínimo ${{ minPurchaseAmount }} alcanzado</span>
                    <span v-else>Mínimo ${{ minPurchaseAmount }} — faltan ${{ (minPurchaseAmount - cartTotalArs).toFixed(0) }}</span>
                  </template>
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="summary-actions">
              <div v-if="showCheckoutForm" class="order-confirm-note">
                <i class="fas fa-envelope me-2"></i>
                Recibirás un email con la confirmación y los pasos a seguir.
              </div>

              <button
                v-if="!showCheckoutForm"
                class="btn btn-primary btn-checkout"
                @click="startCheckout"
                :disabled="(minPurchaseAmount > 0 && !isMinPurchaseMet) || startCheckoutLoading"
                :title="isMinPurchaseMet ? '' : `Monto mínimo $${minPurchaseAmount}`"
              >
                <span v-if="startCheckoutLoading">
                  <i class="fas fa-spinner fa-spin me-2"></i>Preparando checkout...
                </span>
                <span v-else>
                  <i class="fas fa-lock me-2"></i>
                  Iniciar Pedido
                </span>
              </button>

              <button
                v-else
                type="submit"
                class="btn btn-primary btn-checkout"
                @click="submitOrder"
                :disabled="isLoading || !isFormValid"
              >
                <span v-if="isLoading">
                  <i class="fas fa-spinner fa-spin me-2"></i>{{ checkoutStepMessage || 'Procesando...' }}
                </span>
                <span v-else>
                  <i class="fas fa-check me-2"></i>Confirmar Pedido
                </span>
              </button>

              <button
                class="btn btn-light-outline"
                @click="showCheckoutForm ? cancelCheckout() : closeCart()"
              >
                <i class="fas fa-arrow-left me-2"></i>
                {{ showCheckoutForm ? "Volver" : "Seguir Comprando" }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

  </section>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { createOrder, prependUserOrderCache, validateCart } from "@/services/orders";
import { createPayment } from "@/services/payment";
import { validateCouponWithProducts } from "@/services/coupons";
import { getSiteConfig } from "@/services/siteConfig";
import { useToast } from "vue-toastification";
import { useVuelidate } from "@vuelidate/core";
import { required, minLength, helpers } from "@vuelidate/validators";
import { getLoggedInUser } from "@/services/auth";
import "@/assets/css/variables.css";
import { getCurrentDollarRate } from "@/services/payment";
import { fetchCustomerInfoFromTempCart } from "@/services/tempCart";
import apiClient from "@/services/apiClient";
import { getShippingPublicConfig } from "@/services/shipping";
import ShippingQuote from "@/components/shipping/ShippingQuote.vue";
import { loadCheckoutProfile, saveCheckoutProfile } from "@/utils/checkoutProfileStorage";

const store = useStore();
const router = useRouter();
const toast = useToast();
const isLoading = ref(false);
const couponCode = ref("");
const validatingCoupon = ref(false);
const appliedCoupon = ref(null);
const shippingEnabled = ref(false);
const selectedShippingRate = ref(null);
const isCheckoutInProgress = ref(false); // Flag para indicar que estamos en proceso de checkout
const startCheckoutLoading = ref(false);
const checkoutStepMessage = ref("");
const loadingShippingConfig = ref(false);
const isInitializing = ref(true);

// Variables para validación de precios del carrito contra backend
const validatingCart = ref(false);
const cartPriceWarnings = ref([]); // Items con precio cambiado

// Variables para manejar el mínimo de compra
const minPurchaseAmount = ref(0);
const loadingConfig = ref(false);

const cartItems = computed(() => store.getters.cartItems);
const cartSubtotal = computed(() => {
  return cartItems.value.reduce(
    (total, item) => total + item.price * item.quantity,
    0
  );
});

const discountAmount = computed(() => {
  if (!appliedCoupon.value) return 0;

  // Filtrar productos elegibles para el cupón
  const eligibleItems = getEligibleItemsForCoupon();
  
  if (eligibleItems.length === 0) return 0;

  // Calcular subtotal solo de productos elegibles
  const eligibleSubtotal = eligibleItems.reduce(
    (total, item) => total + item.price * item.quantity,
    0
  );

  if (appliedCoupon.value.type === "percentage") {
    // Calcular el descuento basado en porcentaje solo sobre productos elegibles
    const discount = eligibleSubtotal * (appliedCoupon.value.value / 100);

    // Si hay un monto máximo de descuento, limitar el descuento
    if (appliedCoupon.value.max_amount) {
      return Math.min(discount, appliedCoupon.value.max_amount);
    }
    return discount;
  } else {
    // Descuento de monto fijo
    // Asegurar que el descuento no sea mayor que el subtotal elegible
    return Math.min(appliedCoupon.value.value, eligibleSubtotal);
  }
});

// Función para obtener productos elegibles para el cupón
const getEligibleItemsForCoupon = () => {
  if (!appliedCoupon.value) return [];
  if (appliedCoupon.value.eligible_products && appliedCoupon.value.eligible_products.length > 0) {
    const eligibleProductIds = appliedCoupon.value.eligible_products.map(p => p.product_id);
    return cartItems.value.filter(item => eligibleProductIds.includes(item.id));
  }
  
  // Si el cupón no tiene categorías específicas, aplica a todos los productos
  if (!appliedCoupon.value.categories || appliedCoupon.value.categories.length === 0) {
    return cartItems.value;
  }
  
  // Si tenemos información de productos elegibles de la API, usarla
  if (appliedCoupon.value.eligible_products && appliedCoupon.value.eligible_products.length > 0) {
    const eligibleProductIds = appliedCoupon.value.eligible_products.map(p => p.product_id);
    return cartItems.value.filter(item => eligibleProductIds.includes(item.id));
  }
  
  // Fallback: Filtrar productos por categorías del cupón (si tienen category_id)
  return cartItems.value.filter(item => {
    // Verificar si el producto pertenece a alguna de las categorías del cupón
    return item.category_id && appliedCoupon.value.categories.includes(item.category_id);
  });
};

// Computado para mostrar información del descuento por categoría
const discountInfo = computed(() => {
  if (!appliedCoupon.value) return null;
  
  const eligibleItems = getEligibleItemsForCoupon();
  const totalItems = cartItems.value.length;
  const eligibleCount = eligibleItems.length;
  
  return {
    eligibleItems,
    totalItems,
    eligibleCount,
    hasNonEligibleItems: eligibleCount < totalItems,
    eligibleSubtotal: eligibleItems.reduce((total, item) => total + item.price * item.quantity, 0)
  };
});

const selectedShippingCost = computed(() => {
  const isDelivery = orderData.payment_delivery_option?.startsWith("delivery");
  if (!isDelivery || !selectedShippingRate.value || selectedShippingRate.value.freeShippingTesting) {
    return 0;
  }
  return Number(selectedShippingRate.value.price || 0);
});

const finalTotal = computed(() => {
  let total = cartSubtotal.value - discountAmount.value;
  total += selectedShippingCost.value;
  return total;
});

const cartQuantity = computed(() => {
  return cartItems.value.reduce((total, item) => total + item.quantity, 0);
});

// Nuevo: calcular el total del carrito en ARS (conversión segura de USD)
const cartTotalArs = computed(() => {
  let total = 0;
  for (const item of cartItems.value) {
    if (item.currency === 'USD') {
      if (dollarRate.value && dollarRate.value.value) {
        total += convertUsdToArs(item.price * item.quantity);
      } else {
        // Si no hay cotización, no sumar ese producto
        continue;
      }
    } else {
      total += item.price * item.quantity;
    }
  }
  return total;
});

// Modificar la validación del mínimo de compra
const isMinPurchaseMet = computed(() => {
  if (minPurchaseAmount.value <= 0) return true;
  // Si hay productos en USD y no hay cotización, no permitir compra
  const hasUsd = cartItems.value.some(item => item.currency === 'USD');
  if (hasUsd && (!dollarRate.value || !dollarRate.value.value)) return false;
  return cartTotalArs.value >= minPurchaseAmount.value;
});

const dollarRate = ref(null);
const loadingDollarRate = ref(false);

const globalLoading = computed(() => {
  return (
    isInitializing.value ||
    loadingConfig.value ||
    loadingDollarRate.value ||
    loadingShippingConfig.value ||
    validatingCart.value ||
    startCheckoutLoading.value ||
    isLoading.value
  );
});

const globalLoadingText = computed(() => {
  if (isLoading.value && checkoutStepMessage.value) return checkoutStepMessage.value;
  if (startCheckoutLoading.value) return "Validando precios y preparando datos del pedido...";
  if (validatingCart.value) return "Validando precios y stock del carrito...";
  if (loadingConfig.value) return "Cargando configuración de compra...";
  if (loadingDollarRate.value) return "Obteniendo cotización del dólar...";
  if (loadingShippingConfig.value) return "Cargando opciones de envío...";
  return "Preparando carrito...";
});

// Función para convertir USD a ARS de forma segura
const convertUsdToArs = (usd) => {
  if (!dollarRate.value || !dollarRate.value.value) return null;
  const rate = parseFloat(dollarRate.value.value);
  if (isNaN(rate)) return null;
  return usd * rate;
};

// Función para guardar información del cliente con el carrito
const saveCustomerInfo = async () => {
  // Solo sincronizar si hay información del cliente
  if (
    orderData.recipient_name ||
    orderData.customer_email ||
    orderData.customer_phone ||
    orderData.customer_dni_cuit
  ) {
    // Obtener el anonymousId actual del carrito
    const anonymousId = localStorage.getItem('cart_anonymous_id');
    if (!anonymousId) return;
    
    try {
      // Añadir información del cliente al carrito temporal
      await apiClient.post('/orders/public/temp-cart/', {
        anonymous_id: anonymousId,
        items: store.state.cartItems.map(item => ({
          product_id: item.id,
          quantity: item.quantity,
          variants: item.variants || {},
          variants_label: item.variants_label || ''
        })),
        // Información adicional del cliente
        customer_info: {
          name: orderData.recipient_name || '',
          email: orderData.customer_email || '',
          phone: orderData.customer_phone || '',
          dni_cuit: orderData.customer_dni_cuit || '',
          address: showAddressFields.value ? {
            street: addressFields.street || '',
            number: addressFields.number || '',
            city: addressFields.city || '',
            postal_code: addressFields.postalCode || '',
            neighborhood: addressFields.neighborhood || '',
            details: addressFields.details || ''
          } : null
        }
      });
    } catch (error) {
      console.error('Error al guardar información del cliente:', error);
      // No interrumpimos el flujo del usuario si hay un error
    }
    try {
      const addr = addressFields
      const hasAddr =
        [addr.street, addr.number, addr.neighborhood, addr.city, addr.postalCode, addr.details].some(
          (x) => String(x || '').trim()
        )
      saveCheckoutProfile({
        dni_cuit: orderData.customer_dni_cuit || '',
        phone: orderData.customer_phone || '',
        ...(hasAddr
          ? {
              shipping: {
                street: addr.street || '',
                number: addr.number || '',
                neighborhood: addr.neighborhood || '',
                city: addr.city || '',
                postalCode: addr.postalCode || '',
                details: addr.details || '',
              },
            }
          : {}),
      })
    } catch (e) {
      /* no bloquear */
    }
  }
};

// Configuración de debounce para los campos del cliente
const setupCustomerInfoDebounce = () => {
  let debounceTimer;
  
  // Para cada campo de cliente, configurar un watcher con debounce
  ['recipient_name', 'customer_email', 'customer_phone', 'customer_dni_cuit'].forEach(field => {
    watch(() => orderData[field], () => {
      // Limpiar el temporizador anterior
      clearTimeout(debounceTimer);
      
      // Configurar un nuevo temporizador (3 segundos)
      debounceTimer = setTimeout(() => {
        saveCustomerInfo();
      }, 3000);
    });
  });
  
  // También guardar cuando cambian los datos de dirección
  watch(() => addressFields, () => {
    if (showAddressFields.value) {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        saveCustomerInfo();
      }, 3000);
    }
  }, { deep: true });
};

onMounted(async () => {
  isInitializing.value = true;
  // Intentar recuperar carrito del servidor y fusionar con el local
  store.dispatch('mergeLocalAndServerCarts');
  
  // Validar precios del carrito contra backend
  await validateCartPrices();
  
  // Configurar el debounce para los campos del cliente
  setupCustomerInfoDebounce();
  
  // Cargar información del cliente si existe en un carrito abandonado
  try {
    const customerInfo = await fetchCustomerInfoFromTempCart();
    if (customerInfo) {
      // Llenar los campos con la información guardada
      if (customerInfo.name) orderData.recipient_name = customerInfo.name;
      if (customerInfo.email) orderData.customer_email = customerInfo.email;
      if (customerInfo.phone) orderData.customer_phone = customerInfo.phone;
      if (customerInfo.dni_cuit) orderData.customer_dni_cuit = customerInfo.dni_cuit;
      
      // Llenar los campos de dirección si existen
      if (customerInfo.address) {
        if (customerInfo.address.street) addressFields.street = customerInfo.address.street;
        if (customerInfo.address.number) addressFields.number = customerInfo.address.number;
        if (customerInfo.address.city) addressFields.city = customerInfo.address.city;
        if (customerInfo.address.postal_code) addressFields.postalCode = customerInfo.address.postal_code;
        if (customerInfo.address.neighborhood) addressFields.neighborhood = customerInfo.address.neighborhood;
        if (customerInfo.address.details) addressFields.details = customerInfo.address.details;
      }
    }
  } catch (error) {
    console.error('Error al cargar información del cliente guardada:', error);
  }

  applySavedCheckoutProfile();

  // Cargar configuración del sitio (monto mínimo de compra)
  loadingConfig.value = true;
  try {
    const siteConfig = await getSiteConfig();
    minPurchaseAmount.value = parseFloat(siteConfig.min_purchase_amount || 0);
  } catch (error) {
    console.error("Error loading site configuration:", error);
  } finally {
    loadingConfig.value = false;
  }

  // Obtener cotización del dólar
  loadingDollarRate.value = true;
  try {
    dollarRate.value = await getCurrentDollarRate();
  } catch (e) {
    dollarRate.value = null;
    console.error("Error obteniendo cotización del dólar:", e);
  } finally {
    loadingDollarRate.value = false;
  }

  // Verificar si Correo Argentino está habilitado
  loadingShippingConfig.value = true;
  try {
    const shippingConfig = await getShippingPublicConfig();
    shippingEnabled.value = !!shippingConfig?.is_enabled;
  } catch (e) {
    shippingEnabled.value = false;
  } finally {
    loadingShippingConfig.value = false;
  }

  isInitializing.value = false;
});

const onShippingRateSelected = (rate) => {
  selectedShippingRate.value = rate;
};

const formatPrice = (price) => {
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "ARS",
  }).format(price);
};

// Formato para mostrar el descuento del cupón
const formatDiscountText = (coupon) => {
  if (coupon.type === "percentage") {
    let text = `${coupon.value}% de descuento`;
    if (coupon.max_amount) {
      text += ` (hasta ${formatPrice(coupon.max_amount)})`;
    }
    return text;
  } else {
    return `${formatPrice(coupon.value)} de descuento`;
  }
};

// Validar código de cupón
const validateCouponCode = async () => {
  if (!couponCode.value.trim()) return;

  // Si no está logueado, redirigir al login
  if (!localStorage.getItem("accessToken")) {
    toast.info("Debes iniciar sesión para usar un cupón");
    router.push("/iniciar-sesion?redirect=carrito");
    return;
  }

  try {
    validatingCoupon.value = true;
    
    // Preparar datos de los productos del carrito para validación
    const cartProducts = cartItems.value.map(item => ({
      product_id: item.id,
      quantity: item.quantity
    }));
    
    // Usar el método correcto que valida con productos
    const result = await validateCouponWithProducts(
      couponCode.value.trim(),
      cartProducts,
      cartSubtotal.value,
      orderData.customer_email
    );
    if (result.valid) {
      // Guardar el cupón con la información adicional de productos elegibles
      appliedCoupon.value = {
        ...result.coupon,
        eligible_products: result.eligible_products || []
      };
      
      // Verificar si hay productos elegibles devueltos por el backend
      if (result.eligible_products && result.eligible_products.length === 0 && result.coupon.categories && result.coupon.categories.length > 0) {
        toast.error("Este cupón no se puede aplicar a ninguno de los productos en tu carrito. Está destinado a productos de categorías específicas.");
        appliedCoupon.value = null;
        return;
      }
      
      // Mostrar información detallada del cupón aplicado
      let message = `Cupón aplicado: ${formatDiscountText(result.coupon)}`;
      
      if (result.eligible_products && result.eligible_products.length > 0 && result.eligible_products.length < cartItems.value.length) {
        message += ` (aplicado a ${result.eligible_products.length} de ${cartItems.value.length} productos)`;
      }
      
      toast.success(message);
      couponCode.value = "";
    } else {
      toast.error(result.reason || "Cupón no válido");
    }
  } catch (error) {
    console.error("Error validando cupón:", error);
    let errorMessage = "Error al validar el cupón";

    // Mostrar mensaje de error específico si está disponible
    if (error.response && error.response.data) {
      errorMessage =
        error.response.data.reason || error.response.data.error || errorMessage;
    }

    toast.error(errorMessage);
  } finally {
    validatingCoupon.value = false;
  }
};

// Eliminar cupón aplicado
const removeCoupon = () => {
  appliedCoupon.value = null;
  toast.info("Cupón removido");
};

const updateQuantity = async (lineId, quantity) => {
  try {
    // Validar que quantity sea un número válido
    if (isNaN(quantity) || quantity <= 0) {
      console.warn("Cantidad inválida recibida:", quantity);
      // Obtener el valor actual para restaurarlo
      const currentItem = cartItems.value.find((item) => (item.lineId || item.id) === lineId);
      if (currentItem) {
        return; // Mantener la cantidad actual
      }
      quantity = 1; // Fallback a 1 si no se encuentra el item
    }
    
    const item = cartItems.value.find((item) => (item.lineId || item.id) === lineId);
    if (!item) {
      console.error("Item no encontrado:", lineId);
      return;
    }

    // Validar contra el stock del producto directamente
    if (item.stock && quantity > item.stock) {
      toast.error(`Solo hay ${item.stock} unidades disponibles`);
      return;
    }

    const validQuantity = item.stock 
      ? Math.max(1, Math.min(item.stock, quantity))
      : Math.max(1, quantity);
    
    // Despachar la acción de actualización con el ID correcto
    await store.dispatch("updateQuantity", { 
      lineId: item.lineId || item.id, 
      quantity: validQuantity 
    });
    
    // Verificar que el total se actualizó correctamente
    console.log("Cantidad actualizada:", validQuantity);
    console.log("Nuevo subtotal:", cartSubtotal.value);
    
  } catch (error) {
    console.error("Error updating quantity:", error);
    toast.error("Error al actualizar la cantidad");
  }
};

const removeItem = (lineId) => {
  store.dispatch("removeFromCart", lineId);
};

const closeCart = () => {
  store.dispatch("setCartOpen", false);
};

const showCheckoutForm = ref(false);
const orderData = reactive({
  recipient_name: "",
  customer_dni_cuit: "",
  shipping_address: "",
  shipping_street: "",
  shipping_number: "",
  shipping_neighborhood: "",
  shipping_city: "",
  shipping_postal_code: "",
  customer_email: "",
  customer_phone: "",
  delivery_type: "pickup",
  payment_method: "cash",
  payment_delivery_option: "pickup_cash", // Nueva opción combinada
});

const addressFields = reactive({
  street: "",
  number: "",
  neighborhood: "",
  city: "",
  postalCode: "",
  details: "",
});

/**
 * Rellena DNI, teléfono y dirección desde el registro o última edición (solo si el campo sigue vacío).
 */
function applySavedCheckoutProfile() {
  const saved = loadCheckoutProfile();
  if (!saved) return;
  const setIfEmpty = (obj, key, val) => {
    const v = val != null && typeof val === "string" ? val.trim() : val;
    if (v === undefined || v === null || v === "") return;
    if (!String(obj[key] || "").trim()) obj[key] = v;
  };
  setIfEmpty(orderData, "customer_dni_cuit", saved.dni_cuit);
  setIfEmpty(orderData, "customer_phone", saved.phone);
  if (saved.shipping && typeof saved.shipping === "object") {
    const s = saved.shipping;
    setIfEmpty(addressFields, "street", s.street);
    setIfEmpty(addressFields, "number", s.number);
    setIfEmpty(addressFields, "neighborhood", s.neighborhood);
    setIfEmpty(addressFields, "city", s.city);
    setIfEmpty(addressFields, "postalCode", s.postalCode);
    setIfEmpty(addressFields, "details", s.details);
  }
}

// Modificación de las reglas de validación
const rules = {
  recipient_name: {
    required: helpers.withMessage("El nombre es requerido", required),
    minLength: helpers.withMessage(
      "El nombre debe tener al menos 3 caracteres",
      minLength(3)
    ),
  },
  customer_dni_cuit: {
    required: helpers.withMessage("El DNI/CUIT es requerido", required),
    validDocument: helpers.withMessage(
      "Ingresa un DNI o CUIT válido",
      helpers.regex(/^[0-9]{7,11}$/)
    ),
  },
  customer_phone: {
    required: helpers.withMessage("El teléfono es requerido", required),
    minDigits: helpers.withMessage(
      "Ingresá un teléfono válido (mínimo 8 dígitos)",
      (v) => String(v || "").replace(/\D/g, "").length >= 8
    ),
  },
  delivery_type: {
    required: helpers.withMessage("Selecciona un método de entrega", required),
  },
  customer_email: {
    required: helpers.withMessage("El email es requerido", required),
    email: helpers.withMessage(
      "Ingresa un email válido",
      helpers.regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)
    ),
  },
  payment_delivery_option: {
    required: helpers.withMessage(
      "Selecciona una opción de entrega y pago",
      required
    ),
  },
};

// Watch para actualizar los campos cuando cambia la opción de entrega y pago
watch(
  () => orderData.payment_delivery_option,
  (newOption) => {
    // Establecer el tipo de entrega basado en la opción seleccionada
    if (newOption === "pickup_cash" || newOption === "pickup_online") {
      orderData.delivery_type = "pickup";
      orderData.shipping_address = "Coordinar entrega";
    } else {
      orderData.delivery_type = "delivery";
    }

    // Establecer el método de pago basado en la opción seleccionada
    if (newOption === "pickup_cash" || newOption === "delivery_cash") {
      orderData.payment_method = "cash";
    } else {
      orderData.payment_method = "online";
    }

    const isDeliveryOption = newOption?.startsWith("delivery");
    if (!isDeliveryOption) {
      selectedShippingRate.value = null;
    }

    // Revalidar el formulario
    v$.value.$touch();
  }
);

// Update the watch function to sync with shipping fields based on payment_delivery_option
watch(
  [addressFields, () => orderData.payment_delivery_option],
  () => {
    const isDeliveryOption =
      orderData.payment_delivery_option.startsWith("delivery");

    if (isDeliveryOption) {
      const { street, number, neighborhood, city, postalCode, details } =
        addressFields;
      const requiredFields = [street, number, city, postalCode];

      // Verificar si los campos requeridos están completos
      const isAddressValid = requiredFields.every(
        (field) => field && field.trim() !== ""
      );

      if (isAddressValid) {
        // Formatear la dirección completa para el campo shipping_address
        orderData.shipping_address = [
          `${street} ${number}`,
          neighborhood,
          city,
          `CP: ${postalCode}`,
          details,
        ]
          .filter(Boolean)
          .join(", ");

        // Actualizar los campos específicos
        orderData.shipping_street = street;
        orderData.shipping_number = number;
        orderData.shipping_neighborhood = neighborhood;
        orderData.shipping_city = city;
        orderData.shipping_postal_code = postalCode;
      } else {
        orderData.shipping_address = "";
      }
    } else {
      orderData.shipping_address = "Coordinar entrega";
      // Limpiar los campos específicos
      orderData.shipping_street = "";
      orderData.shipping_number = "";
      orderData.shipping_neighborhood = "";
      orderData.shipping_city = "";
      orderData.shipping_postal_code = "";
    }
  },
  { deep: true }
);

const isFormValid = computed(() => {
  // Validar campos básicos requeridos siempre
  if (
    !orderData.recipient_name ||
    !orderData.customer_dni_cuit ||
    !orderData.customer_phone ||
    !orderData.customer_email ||
    !orderData.payment_delivery_option
  ) {
    return false;
  }

  // Si es envío a domicilio, validar los campos de dirección
  if (showAddressFields.value) {
    const { street, number, postalCode, city } = addressFields;
    if (!(street && number && postalCode && city)) return false;
    if (requiresBranchSelection.value) return false;
    return true;
  }

  return true;
});

const v$ = useVuelidate(rules, orderData);

const loadUserData = async () => {
  try {
    const userData = await getLoggedInUser();
    if (userData) {
      // Set recipient name from first_name and last_name, fallback to username
      orderData.recipient_name =
        userData.first_name && userData.last_name
          ? `${userData.first_name} ${userData.last_name}`
          : userData.username;

      // Set email
      orderData.customer_email = userData.email;

      // Set phone if available
      if (userData.phone) {
        orderData.customer_phone = userData.phone;
      }
      if (userData.dni) {
        orderData.customer_dni_cuit = userData.dni;
      }
    }
  } catch (error) {
    console.error("Error loading user data:", error);
  }
};

const validateCartPrices = async () => {
  if (cartItems.value.length === 0) return true;
  validatingCart.value = true;
  cartPriceWarnings.value = [];
  try {
    const result = await validateCart(cartItems.value);
    if (result.has_changes) {
      const warnings = [];
      for (const serverItem of result.items) {
        if (serverItem.price_changed) {
          // Buscar el item en el carrito local para actualizar
          const localItem = cartItems.value.find(
            ci => ci.id === serverItem.product_id
          );
          if (localItem) {
            warnings.push(localItem.name);
            store.commit('UPDATE_CART_ITEM_PRICE', {
              lineId: localItem.lineId,
              price: serverItem.effective_price,
              original_price: serverItem.base_price,
              discount: serverItem.discount_percent,
              total_discount: serverItem.discount_percent,
            });
          }
        }
      }
      cartPriceWarnings.value = warnings;
      if (warnings.length > 0) {
        toast.warning(
          `Los precios de ${warnings.length} producto(s) han cambiado y fueron actualizados: ${warnings.join(', ')}`
        );
      }
      return false; // Changes detected — caller may want to re-check
    }
    return true; // No changes
  } catch (error) {
    console.error('Error validating cart prices:', error);
    return true; // Don't block checkout on validation error
  } finally {
    validatingCart.value = false;
  }
};

const startCheckout = async () => {
  startCheckoutLoading.value = true;

  // Validar mínimo de compra
  if (
    minPurchaseAmount.value > 0 &&
    finalTotal.value < minPurchaseAmount.value
  ) {
    toast.error(
      `El monto mínimo de compra es $${minPurchaseAmount.value}. Tu carrito actual es de $${finalTotal.value}.`
    );
    startCheckoutLoading.value = false;
    return;
  }

  if (!localStorage.getItem("accessToken")) {
    toast.info("Debes iniciar sesión para realizar la compra");
    router.push("/iniciar-sesion?redirect=carrito");
    startCheckoutLoading.value = false;
    return;
  }

  try {
    // Validar precios contra el backend antes de continuar
    await validateCartPrices();

    await loadUserData(); // Cargar datos del usuario
    applySavedCheckoutProfile();
    showCheckoutForm.value = true;
  } finally {
    startCheckoutLoading.value = false;
  }
};

const queuePostOrderEmailModal = (order) => {
  try {
    sessionStorage.setItem(
      "post_order_email_notice",
      JSON.stringify({
        orderId: order.id,
        email: order.customer_email,
        createdAt: new Date().toISOString(),
      })
    );
  } catch (error) {
    console.error("Error guardando aviso post pedido:", error);
  }
};

const cancelCheckout = () => {
  showCheckoutForm.value = false;
  isCheckoutInProgress.value = false;
  selectedShippingRate.value = null;
  // Limpiar campos de dirección
  Object.keys(addressFields).forEach((key) => {
    addressFields[key] = "";
  });
  // Limpiar los campos específicos
  orderData.shipping_street = "";
  orderData.shipping_number = "";
  orderData.shipping_neighborhood = "";
  orderData.shipping_city = "";
  orderData.shipping_postal_code = "";
  // Restablecer opción de pago y entrega predeterminada
  orderData.payment_delivery_option = "pickup_cash";
  orderData.delivery_type = "pickup";
  orderData.payment_method = "cash";
  // Limpiar cupón aplicado
  appliedCoupon.value = null;
  couponCode.value = "";
};

const submitOrder = async () => {
  try {
    const isFormValid = await v$.value.$validate();
    if (!isFormValid) return;

    if (requiresBranchSelection.value) {
      toast.error("Seleccioná la sucursal de retiro para continuar con este tipo de envío.");
      return;
    }

    isLoading.value = true;
    checkoutStepMessage.value = "Validando stock...";

    // Verificar stock
    for (const item of cartItems.value) {
      if (item.quantity > item.stock) {
        toast.error(
          `No hay suficiente stock de ${item.name}. Stock disponible: ${item.stock}`
        );
        isLoading.value = false;
        return;
      }
    }

    // Extraer la información de la opción seleccionada
    const [deliveryType, paymentMethod] =
      orderData.payment_delivery_option.split("_");

    // Preparar la dirección según el tipo de entrega
    const shippingAddress =
      deliveryType === "delivery"
        ? orderData.shipping_address
        : "Coordinar entrega";

    const orderPayload = {
      recipient_name: orderData.recipient_name,
      customer_dni_cuit: orderData.customer_dni_cuit,
      shipping_address: shippingAddress,
      shipping_street: orderData.shipping_street,
      shipping_number: orderData.shipping_number,
      shipping_neighborhood: orderData.shipping_neighborhood,
      shipping_city: orderData.shipping_city,
      shipping_postal_code: orderData.shipping_postal_code,
      customer_phone: orderData.customer_phone,
      customer_email: orderData.customer_email,
      delivery_type: deliveryType,
      payment_method: paymentMethod,
      // Agregar información del cupón si hay uno aplicado
      ...(appliedCoupon.value ? { coupon_code: appliedCoupon.value.code } : {}),
      // Datos de envío Correo Argentino (si se cotizó)
      ...(selectedShippingRate.value && deliveryType === "delivery" ? {
        shipping_cost: selectedShippingCost.value,
        shipping_service: selectedShippingRate.value.productType || "CP",
        shipping_delivery_type: selectedShippingRate.value.deliveredType || selectedShippingRate.value.deliveryType || "D",
        shipping_product_name: selectedShippingRate.value.productName || "",
        shipping_estimated_min: selectedShippingRate.value.deliveryTimeMin || 0,
        shipping_estimated_max: selectedShippingRate.value.deliveryTimeMax || 0,
        ...(selectedShippingRate.value.deliveredType === "S" ? {
          shipping_agency_id: selectedShippingRate.value.branchCode || "",
          shipping_agency_name: (selectedShippingRate.value.branchName || "").slice(0, 255),
        } : {}),
        ...(selectedShippingRate.value.packageDimensions ? {
          shipping_weight_grams: selectedShippingRate.value.packageDimensions.weight,
          shipping_length_cm: selectedShippingRate.value.packageDimensions.length,
          shipping_width_cm: selectedShippingRate.value.packageDimensions.width,
          shipping_height_cm: selectedShippingRate.value.packageDimensions.height,
        } : {}),
      } : {}),
      // Productos necesarios para crear OrderItems en backend
      products: cartItems.value.map((item) => ({
        product_id: item.id,
        quantity: item.quantity,
        price: item.price,
      })),
      // Metadata extendida por línea de pedido (incluye variantes seleccionadas)
      products_data: cartItems.value.map((item) => ({
        product_id: item.id,
        quantity: item.quantity,
        price: item.price,
        currency: item.currency,
        variants: item.variants || {},
        variants_label:
          item.variants && Object.keys(item.variants).length
            ? Object.entries(item.variants)
                .map(([k, v]) => `${k}: ${v}`)
                .join(" | ")
            : "",
      })),
    };

    // Crear la orden en el backend
    checkoutStepMessage.value = "Confirmando pedido...";
    const orderResponse = await createOrder(orderPayload);
    console.log("Order created:", orderResponse);

    if (!orderResponse || !orderResponse.id) {
      throw new Error("Invalid order response from server");
    }

    // El uso del cupón ya se registra automáticamente en el backend
    if (appliedCoupon.value) {
      console.log("Coupon usage recorded automatically by backend");
    }

    // Marcar que estamos en proceso de checkout para evitar que el watch remueva el cupón
    isCheckoutInProgress.value = true;

    // Limpiar el carrito primero
    store.dispatch("clearCart");

    // Sincronizar caché para que aparezca inmediatamente en Mis Pedidos
    prependUserOrderCache(orderResponse);
    queuePostOrderEmailModal(orderResponse);

    // Si el pago es online, redirigir a MercadoPago
    if (paymentMethod === "online") {
      // Iniciar el proceso de pago
      checkoutStepMessage.value = "Creando link de pago...";
      const paymentResponse = await createPayment(orderResponse.id);
      console.log("Payment initiated:", paymentResponse);

      if (!paymentResponse || !paymentResponse.init_point) {
        throw new Error("Invalid payment response from server");
      }

      // Mostrar mensaje de éxito
      toast.success(
        "Pedido creado exitosamente. Redirigiendo al sistema de pagos..."
      );

      // Establecer un pequeño retraso antes de la redirección para permitir que el carrito se limpie
      setTimeout(() => {
        // Redirigir al usuario al punto de pago de MercadoPago
        window.location.href = paymentResponse.init_point;
      }, 1500);
    } else {
      // Es pago en efectivo: confirmar y navegar inmediatamente a Mis Pedidos
      toast.success("¡Pedido aprobado! Ya lo podés ver en Mis Pedidos.");
      router.push("/mi-cuenta/pedidos");
    }
  } catch (error) {
    console.error("Error in submitOrder:", error);
    toast.error(
      error.response?.data?.error ||
        error.message ||
        "Error al procesar la orden"
    );
    isLoading.value = false;
    checkoutStepMessage.value = "";
  }
};

// Función para verificar si un item específico es elegible para el cupón
const isItemEligibleForCoupon = (item) => {
  if (!appliedCoupon.value) return false;
  if (appliedCoupon.value.eligible_products && appliedCoupon.value.eligible_products.length > 0) {
    const eligibleProductIds = appliedCoupon.value.eligible_products.map(p => p.product_id);
    return eligibleProductIds.includes(item.id);
  }
  
  // Si el cupón no tiene categorías específicas, aplica a todos los productos
  if (!appliedCoupon.value.categories || appliedCoupon.value.categories.length === 0) return true;
  
  // Si tenemos información de productos elegibles de la API, usarla
  if (appliedCoupon.value.eligible_products && appliedCoupon.value.eligible_products.length > 0) {
    const eligibleProductIds = appliedCoupon.value.eligible_products.map(p => p.product_id);
    return eligibleProductIds.includes(item.id);
  }
  
  // Fallback: Verificar si el producto pertenece a alguna de las categorías del cupón
  return item.category_id && appliedCoupon.value.categories.includes(item.category_id);
};

// Computados para determinar qué mostrar según la opción elegida
const showAddressFields = computed(() => {
  return (
    orderData.payment_delivery_option === "delivery_cash" ||
    orderData.payment_delivery_option === "delivery_online"
  );
});

const requiresBranchSelection = computed(() => {
  if (!showAddressFields.value || !selectedShippingRate.value) return false;
  return selectedShippingRate.value.deliveredType === "S" && !selectedShippingRate.value.branchCode;
});

const showPickupMessage = computed(() => {
  return (
    orderData.payment_delivery_option === "pickup_cash" ||
    orderData.payment_delivery_option === "pickup_online"
  );
});

const showCashMessage = computed(() => {
  return (
    orderData.payment_delivery_option === "pickup_cash" ||
    orderData.payment_delivery_option === "delivery_cash"
  );
});

const showOnlinePaymentMessage = computed(() => {
  return (
    orderData.payment_delivery_option === "pickup_online" ||
    orderData.payment_delivery_option === "delivery_online"
  );
});

// Función mejorada para mostrar información del cupón
const getCouponDisplayInfo = (coupon) => {
  if (!coupon) return '';
  
  let info = formatDiscountText(coupon);
  
  // Agregar información sobre las categorías específicas si aplica
  if (coupon.categories && coupon.categories.length > 0) {
    const eligibleItems = getEligibleItemsForCoupon();
    if (eligibleItems.length > 0 && eligibleItems.length < cartItems.value.length) {
      info += ` (solo para productos elegibles)`;
    }
  }
  
  return info;
};

// Watch para verificar si el cupón sigue siendo válido cuando cambia el carrito
watch(
  cartItems,
  () => {
    // No ejecutar esta lógica si estamos en proceso de checkout
    if (isCheckoutInProgress.value) return;
    
    if (appliedCoupon.value && appliedCoupon.value.categories && appliedCoupon.value.categories.length > 0) {
      const eligibleItems = getEligibleItemsForCoupon();
      
      // Si no quedan productos elegibles, remover el cupón
      if (eligibleItems.length === 0) {
        toast.warning("El cupón ha sido removido porque no hay productos elegibles en el carrito");
        appliedCoupon.value = null;
      } else {
        // Actualizar la información de productos elegibles si cambió el carrito
        // Esto es importante para mantener la información actualizada
        console.log(`Cupón ${appliedCoupon.value.code} sigue siendo válido para ${eligibleItems.length} productos`);
      }
    }
  },
  { deep: true }
);
</script>

<style scoped>
/* =============================================
   CART SECTION — Layout
   ============================================= */
.cart-section {
  min-height: 100vh;
  background-color: var(--bg-secondary);
  padding-top: 6rem;
  padding-bottom: 4rem;
}

/* =============================================
   PAGE HEADER
   ============================================= */
.cart-page-header {
  margin-bottom: 2rem;
}

.btn-back {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #fff;
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  text-decoration: none;
  transition: all var(--transition-base);
  box-shadow: var(--shadow-xs);
  flex-shrink: 0;
}

.btn-back:hover {
  background: var(--primary-color);
  color: #fff;
  border-color: var(--primary-color);
  transform: translateX(-2px);
}

.cart-page-title {
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  line-height: 1.1;
}

.cart-page-subtitle {
  font-size: 0.9rem;
  color: var(--text-muted);
  margin: 0.2rem 0 0;
}

.cart-loading-banner {
  display: flex;
  align-items: center;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1e3a8a;
  border-radius: 10px;
  padding: 0.7rem 0.9rem;
  margin-bottom: 1.2rem;
  font-size: 0.86rem;
  font-weight: 600;
}

/* =============================================
   EMPTY STATE
   ============================================= */
.empty-cart-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 5rem 2rem;
  text-align: center;
}

.empty-cart-icon {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: var(--brand-100);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
}

.empty-cart-icon i {
  font-size: 2.5rem;
  color: var(--primary-color);
}

.empty-cart-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
}

.empty-cart-text {
  color: var(--text-muted);
  margin-bottom: 2rem;
  font-size: 1rem;
}

/* =============================================
   CART ITEMS PANEL
   ============================================= */
.cart-items-panel {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.cart-item-card {
  background: #fff;
  border-radius: 14px;
  border: 1px solid var(--border-light);
  box-shadow: var(--shadow-xs);
  overflow: hidden;
  position: relative;
  transition: box-shadow var(--transition-base), transform var(--transition-base);
}

.cart-item-card:hover {
  box-shadow: var(--shadow-md);
  transform: translateY(-1px);
}

.cart-item-card.coupon-eligible {
  border-left: 3px solid var(--primary-color);
}

/* Coupon ribbon */
.coupon-ribbon {
  background: var(--primary-color);
  color: #fff;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 3px 12px;
  letter-spacing: 0.03em;
}

.cart-item-inner {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  align-items: flex-start;
}

/* Product Image */
.cart-item-image-wrap {
  position: relative;
  flex-shrink: 0;
  width: 90px;
  height: 90px;
  border-radius: 10px;
  overflow: hidden;
  background: var(--gray-100);
  border: 1px solid var(--border-light);
}

.cart-item-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: transform var(--transition-base);
  padding: 4px;
}

.cart-item-card:hover .cart-item-image {
  transform: scale(1.06);
}

.item-discount-pill {
  position: absolute;
  top: 5px;
  left: 5px;
  background: var(--danger-color);
  color: #fff;
  font-size: 0.65rem;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 20px;
  line-height: 1.4;
}

/* Product Info */
.cart-item-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.cart-item-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
}

.cart-item-name-wrap {
  flex: 1;
  min-width: 0;
}

.cart-item-name {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 2px;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cart-item-brand {
  font-size: 0.78rem;
  color: var(--text-muted);
  display: block;
}

.cart-item-variants {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 5px;
}

.variant-chip {
  background: var(--gray-100);
  color: var(--text-secondary);
  font-size: 0.7rem;
  padding: 2px 8px;
  border-radius: 20px;
  border: 1px solid var(--border-color);
}

.promo-badge {
  display: inline-flex;
  align-items: center;
  background: #fff8e1;
  color: #b45309;
  border: 1px solid #fcd34d;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 20px;
  margin-top: 5px;
  animation: promo-glow 2s ease-in-out infinite alternate;
}

@keyframes promo-glow {
  from { box-shadow: 0 0 4px rgba(251, 191, 36, 0.4); }
  to   { box-shadow: 0 0 8px rgba(251, 191, 36, 0.7); }
}

.btn-remove-item {
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  transition: all var(--transition-fast);
  flex-shrink: 0;
}

.btn-remove-item:hover {
  background: #fee2e2;
  color: var(--danger-color);
}

/* Footer: qty + price */
.cart-item-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

/* Quantity Stepper */
.qty-stepper {
  display: flex;
  align-items: center;
  background: var(--gray-100);
  border-radius: 8px;
  border: 1px solid var(--border-color);
  overflow: hidden;
  height: 34px;
}

.qty-btn {
  background: none;
  border: none;
  width: 30px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-secondary);
  font-size: 0.7rem;
  transition: all var(--transition-fast);
}

.qty-btn:hover:not(:disabled) {
  background: var(--primary-color);
  color: #fff;
}

.qty-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.qty-input {
  width: 36px;
  height: 34px;
  border: none;
  background: #fff;
  text-align: center;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-primary);
  appearance: textfield;
  -moz-appearance: textfield;
  border-left: 1px solid var(--border-color);
  border-right: 1px solid var(--border-color);
}

.qty-input:focus {
  outline: none;
  background: var(--brand-50);
}

.qty-input::-webkit-inner-spin-button,
.qty-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
}

/* Price block */
.cart-item-price-block {
  text-align: right;
}

.item-original-price {
  font-size: 0.75rem;
  color: var(--text-muted);
  text-decoration: line-through;
  line-height: 1.2;
}

.item-final-price {
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1.3;
}

.usd-price {
  cursor: help;
}

.ars-equiv {
  display: block;
  font-size: 0.72rem;
  color: var(--primary-color);
  font-weight: 500;
}

.item-unit-price {
  font-size: 0.72rem;
  color: var(--text-muted);
  margin-top: 1px;
}

/* =============================================
   SUMMARY PANEL
   ============================================= */
.summary-panel {
  background: #fff;
  border-radius: 16px;
  border: 1px solid var(--border-light);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}

@media (min-width: 992px) {
  .summary-panel {
    position: sticky;
    top: 5.5rem;
  }
}

/* =============================================
   CHECKOUT FORM
   ============================================= */
.checkout-form {
  padding: 1.5rem 1.5rem 0;
  border-bottom: 1px solid var(--border-light);
  margin-bottom: 0;
}

/* Step indicator */
.checkout-steps {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  margin-bottom: 1.5rem;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  opacity: 0.35;
}

.step.active {
  opacity: 1;
}

.step-num {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--gray-200);
  color: var(--text-secondary);
  font-size: 0.75rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.step.active .step-num {
  background: var(--primary-color);
  color: #fff;
}

.step-label {
  font-size: 0.65rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.step.active .step-label {
  color: var(--primary-color);
}

.step-line {
  flex: 1;
  height: 2px;
  background: var(--border-color);
  margin: 0 6px;
  margin-bottom: 18px;
  min-width: 20px;
}

/* Form sections */
.form-section {
  background: var(--gray-50);
  border-radius: 10px;
  padding: 1.25rem;
  border: 1px solid var(--border-light);
}

.form-section-header {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 1rem;
}

.form-section-icon {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: var(--brand-100);
  color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  flex-shrink: 0;
}

.form-section-title {
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--text-primary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 0;
}

.form-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-secondary);
  margin-bottom: 0.35rem;
}

.form-control {
  background: #fff;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 0.875rem;
  padding: 0.5rem 0.75rem;
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);
}

.form-control:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(var(--primary-color-rgb), 0.12);
  outline: none;
  background: #fff;
  color: var(--text-primary);
}

.input-with-icon {
  position: relative;
}

.input-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
  font-size: 0.8rem;
  pointer-events: none;
}

.form-control-icon {
  padding-left: 2rem;
}

.field-error {
  font-size: 0.75rem;
  color: var(--danger-color);
  margin-top: 0.25rem;
}

/* Delivery option cards */
.delivery-cards {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.6rem;
}

.delivery-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  padding: 0.75rem 0.5rem;
  border-radius: 10px;
  border: 2px solid var(--border-color);
  background: #fff;
  cursor: pointer;
  transition: all var(--transition-fast);
  text-align: center;
  position: relative;
}

.delivery-card:hover {
  border-color: var(--primary-light);
  background: var(--brand-50);
}

.delivery-card.selected {
  border-color: var(--primary-color);
  background: var(--brand-50);
  box-shadow: 0 0 0 3px rgba(var(--primary-color-rgb), 0.1);
}

.delivery-card-radio {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.delivery-card-icons {
  display: flex;
  align-items: center;
  gap: 4px;
  color: var(--text-muted);
  font-size: 0.9rem;
}

.delivery-card.selected .delivery-card-icons {
  color: var(--primary-color);
}

.delivery-card-text {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.delivery-card-title {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1.2;
}

.delivery-card-desc {
  font-size: 0.68rem;
  color: var(--text-muted);
  line-height: 1.2;
}

.delivery-card-check {
  color: var(--primary-color);
  font-size: 0.85rem;
  opacity: 0;
  transition: opacity var(--transition-fast);
}

.delivery-card.selected .delivery-card-check {
  opacity: 1;
}

/* Address section */
.address-section {
  background: var(--gray-50);
  border-radius: 8px;
  padding: 1rem;
  border: 1px solid var(--border-light);
}

.address-fields-grid {
  margin-top: 0.75rem;
}

/* Delivery info messages */
.delivery-info-msg {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border-radius: 8px;
  padding: 0.65rem 0.85rem;
  font-size: 0.8rem;
  font-weight: 500;
}

.delivery-info-msg.info {
  background: #eff6ff;
  color: #1e40af;
  border: 1px solid #bfdbfe;
}

.delivery-info-msg.warning {
  background: #fffbeb;
  color: #92400e;
  border: 1px solid #fcd34d;
}

.delivery-info-msg.success {
  background: var(--success-bg);
  color: var(--success-text);
  border: 1px solid var(--success-border);
}

/* =============================================
   SUMMARY DETAILS
   ============================================= */
.summary-details-panel {
  padding: 1.5rem;
}

.summary-title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 1.25rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.summary-line {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  font-size: 0.875rem;
  border-bottom: 1px solid var(--border-light);
}

.summary-line-label {
  color: var(--text-muted);
}

.summary-line-value {
  font-weight: 600;
  color: var(--text-primary);
}

.discount-line .summary-line-value {
  color: var(--primary-color);
}

/* Coupon */
.coupon-section {
  margin: 0.75rem 0;
}

.coupon-input-group {
  display: flex;
  align-items: center;
  border: 1.5px solid var(--border-color);
  border-radius: 10px;
  overflow: hidden;
  background: var(--gray-50);
  transition: border-color var(--transition-fast);
}

.coupon-input-group:focus-within {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(var(--primary-color-rgb), 0.1);
}

.coupon-icon {
  padding: 0 0.75rem;
  color: var(--text-muted);
  font-size: 0.85rem;
}

.coupon-input {
  flex: 1;
  border: none;
  background: transparent;
  padding: 0.55rem 0;
  font-size: 0.85rem;
  color: var(--text-primary);
  outline: none;
}

.coupon-input::placeholder {
  color: var(--text-muted);
}

.coupon-apply-btn {
  background: var(--primary-color);
  color: #fff;
  border: none;
  padding: 0.55rem 1rem;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: background var(--transition-fast);
  white-space: nowrap;
}

.coupon-apply-btn:hover:not(:disabled) {
  background: var(--primary-dark);
}

.coupon-apply-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.coupon-applied {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--brand-50);
  border: 1px solid var(--brand-200);
  border-radius: 10px;
  padding: 0.65rem 0.85rem;
}

.coupon-applied-info {
  display: flex;
  align-items: flex-start;
  gap: 0.25rem;
  font-size: 0.82rem;
  color: var(--text-primary);
}

.coupon-code-text {
  color: var(--primary-color);
  font-weight: 700;
}

.coupon-desc {
  color: var(--text-secondary);
}

.coupon-partial-note {
  font-size: 0.72rem;
  color: var(--text-muted);
  margin-top: 2px;
}

.coupon-remove-btn {
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  transition: all var(--transition-fast);
  flex-shrink: 0;
}

.coupon-remove-btn:hover {
  background: #fee2e2;
  color: var(--danger-color);
}

/* Total row */
.summary-total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0 0.5rem;
  border-top: 2px solid var(--border-color);
  margin-top: 0.5rem;
}

.summary-total-label {
  font-size: 1rem;
  font-weight: 700;
  color: var(--text-primary);
}

.summary-total-value {
  font-size: 1.4rem;
  font-weight: 800;
  color: var(--primary-color);
}

.summary-total-original {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-muted);
  text-decoration: line-through;
  margin-bottom: 2px;
}

/* Min purchase bar */
.min-purchase-bar {
  margin-top: 0.75rem;
}

.min-purchase-info {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.78rem;
  font-weight: 500;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
}

.min-purchase-info.met {
  background: var(--brand-50);
  color: var(--primary-color);
  border: 1px solid var(--brand-200);
}

.min-purchase-info.unmet {
  background: #fef2f2;
  color: var(--danger-color);
  border: 1px solid #fecaca;
}

/* =============================================
   ACTION BUTTONS
   ============================================= */
.summary-actions {
  padding: 1.25rem 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  border-top: 1px solid var(--border-light);
}

.order-confirm-note {
  background: var(--success-bg);
  border: 1px solid var(--success-border);
  border-radius: 8px;
  padding: 0.65rem 0.85rem;
  font-size: 0.78rem;
  color: var(--primary-color);
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.btn-checkout {
  width: 100%;
  padding: 0.85rem;
  font-size: 1rem !important;
  font-weight: 700 !important;
  border-radius: 10px !important;
  letter-spacing: 0.02em;
}

.btn-secondary-outline {
  width: 100%;
  padding: 0.75rem;
  font-size: 0.9rem !important;
  font-weight: 600 !important;
  border-radius: 10px !important;
  background: transparent;
  border: 1.5px solid var(--border-color);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all var(--transition-base);
}

.btn-secondary-outline:hover {
  border-color: var(--primary-color);
  color: var(--primary-color);
  background: var(--brand-50);
}

/* =============================================
   TRANSITION ANIMATIONS
   ============================================= */
.list-move,
.list-enter-active,
.list-leave-active {
  transition: all 0.4s ease;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateX(20px);
}

.list-leave-active {
  position: absolute;
}

/* =============================================
   RESPONSIVE
   ============================================= */
@media (max-width: 991.98px) {
  .cart-section {
    padding-top: 5rem;
  }
}

@media (max-width: 767px) {
  .cart-section {
    padding-top: 4.5rem;
    padding-bottom: 2rem;
  }

  .cart-page-title {
    font-size: 1.5rem;
  }

  .cart-item-image-wrap {
    width: 72px;
    height: 72px;
  }

  .cart-item-name {
    font-size: 0.875rem;
  }

  .delivery-cards {
    grid-template-columns: 1fr 1fr;
    gap: 0.5rem;
  }

  .delivery-card {
    padding: 0.6rem 0.4rem;
  }

  .delivery-card-title {
    font-size: 0.7rem;
  }

  .delivery-card-desc {
    font-size: 0.62rem;
  }

  .summary-total-value {
    font-size: 1.2rem;
  }
}

@media (max-width: 400px) {
  .delivery-cards {
    grid-template-columns: 1fr;
  }
}
</style>
